import { Box, Stack, Switch, TextField, Typography } from '@chronosphereio/chrono-ui';
import { produce } from 'immer';
import { useCallback } from 'react';
import { GaugeChartStepOption, GaugeChartThresholdOptions } from '@/core';
import {
  ThresholdsEditor,
  ThresholdsEditorProps,
} from '@/plugin-editor-components/components/ThresholdsEditor/NewThresholdsEditor';

export const GaugeChartThresholdsEditor = ({
  thresholds,
  onChange,
}: {
  thresholds?: GaugeChartThresholdOptions;
  onChange: (newThresholds: GaugeChartThresholdOptions) => void;
}) => {
  const props: ThresholdsEditorProps<GaugeChartStepOption, GaugeChartThresholdOptions> = {
    onChange,
    renderStepOptions: ({ step, onStepChange }) => {
      return <GaugeChartThresholdStepEditor step={step} onStepChange={onStepChange} />;
    },
    thresholds,
  };

  return <ThresholdsEditor {...props} />;
};

function GaugeChartThresholdStepEditor<T extends Omit<GaugeChartStepOption, 'value'>>({
  step,
  onStepChange,
}: {
  step: T;
  onStepChange: (newStep: T) => void;
}) {
  const handleMessageChange = useCallback(
    (newMessage: string) => {
      onStepChange(
        produce(step, (draft) => {
          return { ...draft, name: newMessage };
        })
      );
    },
    [step, onStepChange]
  );

  const handleOnlyShowMessageSwitch = useCallback(
    (onlyShowMessage: boolean) => {
      onStepChange(
        produce(step, (draft) => {
          draft.show_value = !onlyShowMessage;
        })
      );
    },
    [onStepChange, step]
  );

  return (
    <Stack>
      <TextField
        defaultValue={step.name || ''}
        label="Message"
        fullWidth
        onBlur={(event) => handleMessageChange(event.currentTarget.value)}
      />

      <Stack direction="row" alignItems="center">
        <Box flexGrow={1}>
          <Typography>Show only message</Typography>
        </Box>
        <Box flexGrow={0}>
          <Switch checked={step.show_value === false} onChange={(_, state) => handleOnlyShowMessageSwitch(state)} />
        </Box>
      </Stack>
    </Stack>
  );
}
